import React, { useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { updateEmployee } from '../../../employees/actions/update-employee';
import { bulkRetryMessage } from '../../../messages/actions/bulk-retry-message';
import { retryMessage } from '../../../messages/actions/retry-message';

import { useApi } from '../../../api/use-api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';
import { UiMessageBanner } from '../../../ui/ui-message-banner';

import { MessageDeliveryDetails } from './components/message-delivery-details';
import { MessageDeliveryDetailsEditable } from './components/message-delivery-details-editable';

import { employeeNameFromSimpleContact } from '../../../employees/utils/employee-name';
import { isTruthy } from '../../../util/is-truthy';
import { MESSAGE_TYPE } from '../../../util/message-types';
import { isPhoneNumber } from '../../../util/string-validators';
import { confirm } from '../../../widgets/confirmation-dialog';
import { LoadingMessage } from '../../../widgets/loading-message';

// NOTE: This array is duplicated in twilio-client.ts on the server, and it should be kept up to date with it
const INVALID_PHONE_ERROR_CODES = [
  30005, // Message Delivery - Unknown destination handset (https://www.twilio.com/docs/api/errors/30005)
  30006, // Landline or unreachable carrier (https://www.twilio.com/docs/api/errors/30006)
  21610, // Attempt to send to unsubscribed recipient (https://www.twilio.com/docs/api/errors/21610)
  21211, // Invalid 'To' Phone Number (https://www.twilio.com/docs/api/errors/21211)
];

type InputProps = {
  announcementId: number;
  onClose: () => void;
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    updatePhoneNumber: (employeeId: number, phoneNumber: string) =>
      dispatch(
        updateEmployee(employeeId, {
          Contact: { Person: { phoneNumber } },
        }),
      ),
    retrySendMessage: (messageId: number) => dispatch(retryMessage(messageId)),
    bulkRetrySendMessages: (messageIds: Array<number>) => dispatch(bulkRetryMessage(messageIds)),
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const AnnouncementStatusDialog = connect(
  undefined,
  mapDispatchToProps,
)((props: Props) => {
  const { announcementId, onClose, actions } = props;

  const [isSending, setIsSending] = useState<boolean>(false);
  const [resentMessageIds, setResentMessageId] = useState<Array<number>>([]);

  const { data, loading } = useApi<AnnouncementDeliverabilityDetail[]>({
    url: `/announcements/${announcementId}/deliverability`,
  });

  const {
    successfulDetails,
    successfulTextDetails,
    successfulEmailDetails,
    invalidPhoneDetails,
    failedDetails,
    pendingDetails,
  } = (data || []).reduce(
    (acc, announcementDetail) => {
      if (announcementDetail.isPending) {
        acc.pendingDetails.push(announcementDetail);
      } else if (!announcementDetail.isFailed) {
        acc.successfulDetails.push(announcementDetail);
        if (announcementDetail.messageType === MESSAGE_TYPE.TEXT) {
          acc.successfulTextDetails.push(announcementDetail);
        } else {
          acc.successfulEmailDetails.push(announcementDetail);
        }
      } else if (
        !isPhoneNumber(announcementDetail.phoneNumber) ||
        (announcementDetail.errorCode &&
          INVALID_PHONE_ERROR_CODES.includes(announcementDetail.errorCode))
      ) {
        acc.invalidPhoneDetails.push(announcementDetail);
      } else {
        acc.failedDetails.push(announcementDetail);
      }
      return acc;
    },
    {
      successfulDetails: [] as Array<AnnouncementDeliverabilityDetail>,
      successfulTextDetails: [] as Array<AnnouncementDeliverabilityDetail>,
      successfulEmailDetails: [] as Array<AnnouncementDeliverabilityDetail>,
      invalidPhoneDetails: [] as Array<AnnouncementDeliverabilityDetail>,
      failedDetails: [] as Array<AnnouncementDeliverabilityDetail>,
      pendingDetails: [] as Array<AnnouncementDeliverabilityDetail>,
    },
  );

  const handleRetrySendMessage = async (messageDetail: AnnouncementDeliverabilityDetail) => {
    if (!messageDetail.messageId) {
      return false;
    }

    // If this employee was deleted or terminated, have the user confirm that they want to send
    // the message before retrying
    if (!messageDetail.canRetrySend) {
      const employeeName = employeeNameFromSimpleContact({
        nickname: messageDetail.nickname,
        firstName: messageDetail.firstName,
        lastName: messageDetail.lastName,
      });
      if (
        await confirm(
          `${employeeName} is not a current employee, are you sure you want to resend this announcement to them?`,
        )
      ) {
        actions.retrySendMessage(messageDetail.messageId);
        setResentMessageId(resentMessageIds.concat([messageDetail.messageId]));
        return true;
      }
      return false;
    }

    actions.retrySendMessage(messageDetail.messageId);
    setResentMessageId(resentMessageIds.concat([messageDetail.messageId]));
    return true;
  };

  const handleRetryAllFailed = async () => {
    setIsSending(true);

    const allFailedMessageIds = failedDetails
      .concat(invalidPhoneDetails)
      .filter(
        (message) =>
          message.messageId &&
          !resentMessageIds.includes(message.messageId) &&
          message.canRetrySend,
      )
      .map((m) => m.messageId)
      .filter(isTruthy);

    await actions.bulkRetrySendMessages(allFailedMessageIds);

    setIsSending(false);
    onClose();
  };

  const getMessageBannerVariant = () => {
    if (pendingDetails.length > failedDetails.length) {
      return 'attention';
    }
    return failedDetails.length ? 'error' : 'informational';
  };

  const pluralize = (items: Array<any>) => (items.length === 1 ? '' : 's');

  return (
    <UiDialog onClose={onClose} title="Message Delivery Details" size="sm">
      {loading ? (
        <UiDialog.Content>
          <LoadingMessage />
        </UiDialog.Content>
      ) : (
        <>
          <UiDialog.Content>
            <UiMessageBanner
              className="u-margin-bottom"
              variant={getMessageBannerVariant()}
              message={
                pendingDetails.length
                  ? `Delivered ${successfulDetails.length} Message${pluralize(
                      successfulDetails,
                    )}, ${pendingDetails.length} Message${pluralize(pendingDetails)} Pending`
                  : `Delivered ${successfulDetails.length} of ${(data || []).length} Messages`
              }
            />
            {invalidPhoneDetails.length ? (
              <MessageDeliveryDetailsEditable
                title={`${invalidPhoneDetails.length} Invalid Phone Number`}
                messageDetails={invalidPhoneDetails}
                updatePhoneNumber={actions.updatePhoneNumber}
                retrySendMessage={handleRetrySendMessage}
              />
            ) : null}
            {failedDetails.length ? (
              <MessageDeliveryDetailsEditable
                title={`${failedDetails.length} Failed to Send`}
                messageDetails={failedDetails}
                updatePhoneNumber={actions.updatePhoneNumber}
                retrySendMessage={handleRetrySendMessage}
              />
            ) : null}
            {pendingDetails.length ? (
              <MessageDeliveryDetails
                title={`${pendingDetails.length} Pending`}
                messageDetails={pendingDetails}
              />
            ) : null}
            {successfulTextDetails.length ? (
              <MessageDeliveryDetails
                title={`${successfulTextDetails.length} Delivered via Text`}
                messageDetails={successfulTextDetails}
              />
            ) : null}
            {successfulEmailDetails.length ? (
              <MessageDeliveryDetails
                title={`${successfulEmailDetails.length} Delivered via Email`}
                messageDetails={successfulEmailDetails}
              />
            ) : null}
          </UiDialog.Content>
          <UiDialog.Actions>
            <UiButton onClick={onClose} variant="tertiary">
              Close
            </UiButton>
            <UiButton
              onClick={handleRetryAllFailed}
              loading={isSending}
              disabled={!failedDetails.length && !invalidPhoneDetails.length}
            >
              Retry Failed
            </UiButton>
          </UiDialog.Actions>
        </>
      )}
    </UiDialog>
  );
});
